import { useState, useEffect } from "react";
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, GripVertical, Save, X, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { toast } from "sonner";
import { AdminLoadingState } from "../components/admin/AdminErrorBoundary";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return localStorage.getItem("adminToken") || "";
}
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

function FAQEditor({ faq, onSave, onCancel }) {
  const [question, setQuestion] = useState(faq?.question || "");
  const [answer, setAnswer] = useState(faq?.answer || "");
  const [type, setType] = useState(faq?.type || "text");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!question.trim()) { toast.error("Question is required"); return; }
    setSaving(true);
    await onSave({ question, answer, type, visible: faq?.visible ?? true });
    setSaving(false);
  };

  return (
    <div className="bg-white border border-[#9A7B4F]/20 p-5" data-testid="faq-editor">
      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Question</label>
          <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="Enter FAQ question..."
            className="w-full border border-[#282828]/12 px-3 py-2.5 text-[13px] focus:border-[#9A7B4F] focus:outline-none" data-testid="faq-question-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Type</label>
          <div className="flex gap-2">
            {[{ key: "text", label: "Text Answer" }, { key: "size-guide", label: "Size Guide" }].map(t => (
              <button key={t.key} onClick={() => setType(t.key)}
                className={`px-3 py-1.5 text-[11px] border ${type === t.key ? 'bg-[#9A7B4F] text-white border-[#9A7B4F]' : 'bg-white text-[#676664] border-[#282828]/12'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        {type === "text" && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Answer</label>
            <textarea value={answer} onChange={e => setAnswer(e.target.value)} rows={6} placeholder="Enter FAQ answer..."
              className="w-full border border-[#282828]/12 px-3 py-2.5 text-[13px] focus:border-[#9A7B4F] focus:outline-none resize-none" data-testid="faq-answer-input" />
            <p className="text-[9px] text-[#676664]/35 mt-1">Use line breaks for paragraphs</p>
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-4 justify-end">
        <button onClick={onCancel} className="px-4 py-1.5 text-[11px] text-[#676664] border border-[#282828]/12">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="px-6 py-1.5 bg-[#9A7B4F] text-white text-[11px] tracking-wider uppercase" data-testid="faq-save-btn">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
        </button>
      </div>
    </div>
  );
}

export default function AdminFAQs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/admin/faqs`, { headers: headers() });
      setFaqs(res.data.faqs || []);
    } catch { toast.error("Failed to load FAQs"); }
    setLoading(false);
  };

  useEffect(() => { fetchFaqs(); }, []);

  const handleCreate = async (data) => {
    try {
      const maxOrder = (faqs || []).length > 0 ? Math.max(...(faqs || []).map(f => f.order || 0)) : -1;
      await axios.post(`${API}/admin/faqs`, { ...data, order: maxOrder + 1 }, { headers: headers() });
      toast.success("FAQ created");
      setCreating(false);
      fetchFaqs();
    } catch { toast.error("Failed to create"); }
  };

  const handleUpdate = async (faqId, data) => {
    try {
      await axios.put(`${API}/admin/faqs/${faqId}`, data, { headers: headers() });
      toast.success("FAQ updated");
      setEditingId(null);
      fetchFaqs();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await axios.delete(`${API}/admin/faqs/${faqId}`, { headers: headers() });
      toast.success("FAQ deleted");
      fetchFaqs();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleVisibility = async (faq) => {
    await handleUpdate(faq.id, { visible: !faq.visible });
  };

  const moveItem = async (index, direction) => {
    const newFaqs = [...faqs];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newFaqs.length) return;
    [newFaqs[index], newFaqs[swapIndex]] = [newFaqs[swapIndex], newFaqs[index]];
    setFaqs(newFaqs);
    try {
      await axios.put(`${API}/admin/faqs/reorder`, { order: newFaqs.map(f => f.id) }, { headers: headers() });
    } catch { toast.error("Failed to reorder"); fetchFaqs(); }
  };

  if (loading) return <AdminLoadingState message="Loading FAQs..." />;

  return (
    <AdminLayout
      title="FAQ Management"
      subtitle="Add, edit, reorder, and toggle visibility of FAQs"
      actions={
        <Button onClick={() => { setCreating(true); setEditingId(null); }} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-5" data-testid="add-faq-btn">
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      }
    >

      {creating && (
        <div className="mb-4">
          <FAQEditor faq={{}} onSave={handleCreate} onCancel={() => setCreating(false)} />
        </div>
      )}

      <div className="space-y-2">
        {(faqs || []).map((faq, index) => (
          <div key={faq.id} data-testid={`faq-item-${faq.id}`}>
            {editingId === faq.id ? (
              <FAQEditor faq={faq} onSave={(data) => handleUpdate(faq.id, data)} onCancel={() => setEditingId(null)} />
            ) : (
              <div className={`bg-white border border-[#282828]/8 p-4 flex items-center gap-3 ${!faq.visible ? 'opacity-50' : ''}`}>
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button onClick={() => moveItem(index, -1)} disabled={index === 0} className="p-0.5 text-[#676664]/30 hover:text-[#676664] disabled:opacity-20">
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => moveItem(index, 1)} disabled={index === (faqs || []).length - 1} className="p-0.5 text-[#676664]/30 hover:text-[#676664] disabled:opacity-20">
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-[#282828] font-medium truncate">{faq.question}</p>
                  <p className="text-[11px] text-[#676664]/40 mt-0.5 truncate">
                    {faq.type === "size-guide" ? "Type: Size Guide" : faq.answer?.slice(0, 80) + (faq.answer?.length > 80 ? "..." : "")}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => toggleVisibility(faq)} className={`p-1.5 transition-colors ${faq.visible ? 'text-green-500/50 hover:text-green-600' : 'text-red-400/50 hover:text-red-500'}`} title={faq.visible ? "Hide" : "Show"} data-testid={`toggle-faq-${faq.id}`}>
                    {faq.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => { setEditingId(faq.id); setCreating(false); }} className="p-1.5 text-[#676664]/30 hover:text-[#9A7B4F]" data-testid={`edit-faq-${faq.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(faq.id)} className="p-1.5 text-[#676664]/30 hover:text-red-400" data-testid={`delete-faq-${faq.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
